import { ApolloLink, } from 'apollo-link';
import { RetryLink, } from 'apollo-link-retry';

/**
 * Retry policy for server-side GraphQL requests.
 *
 * A server-side request leaves the cluster through the public host name, and
 * then it comes back through the CDN. The CDN sometimes closes the connection
 * before it sends a reply. Node reports this event as `ECONNRESET`, or as a
 * socket hang up. One closed connection makes the Apollo error link put status
 * 503 on the whole page. Therefore one more attempt is of value.
 *
 * Two rules keep the retry safe:
 *
 * - The policy retries a closed connection only. If the policy also retried a
 *   request that its own abort signal stopped, each attempt would add its own
 *   timeout to the total time. An earlier version of this retry did that, and a
 *   slow reply became a page that never rendered.
 * - All attempts share one deadline. The CDN gives the origin approximately 15
 *   seconds to send the first byte, and then it disconnects the client. All
 *   attempts, and the delay between them, must end inside that limit.
 *
 * Each function here is total. No function throws, because the retry runs on a
 * path that already failed, and a second failure there hides the first one.
 */

/** Maximum time for one attempt. */
export const SSR_FETCH_TIMEOUT_MS = 10000;

/** Total time for all attempts. This value stays below the CDN limit. */
export const SSR_RETRY_BUDGET_MS = 12000;

/** Minimum time that must remain before the policy makes one more attempt. */
const SSR_RETRY_MIN_REMAINING_MS = 3000;

/** Minimum time for one attempt, so a nearly spent budget still gives a try. */
export const SSR_MIN_ATTEMPT_MS = 1000;

/** Number of attempts for one operation. This count includes the first one. */
const SSR_RETRY_MAX_ATTEMPTS = 2;

/**
 * Delay between attempts. A closed connection fails at once, so the delay only
 * prevents all pods from making the next attempt at the same moment.
 */
const SSR_RETRY_DELAY = { initial: 100, max: 500, jitter: true, };

/** Name of the context property that holds the shared deadline. */
const SSR_DEADLINE_KEY = 'ssrDeadlineAt';

const CONNECTION_RESET_PATTERN = /socket hang up|ECONNRESET/i;

/**
 * Reads the text of an error, whatever form the error has.
 *
 * A network layer usually gives an error as an object with a message. It
 * sometimes gives a plain string. Both forms must give the same result.
 *
 * @param {*} error the error to read
 * @returns {string} the error text, or an empty string
 */
function errorText(error) {
  if (typeof error === 'string') return error;

  try {
    const { message, } = error;

    return typeof message === 'string' ? message : '';
  }
  catch (readFailure) {
    return '';
  }
}

/**
 * Is this value usable for arithmetic?
 *
 * @param {*} value the value to test
 * @returns {boolean} true if the value is a finite number
 */
function isUsableTime(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Did the peer close the connection?
 *
 * A status code shows that the peer sent a reply, so the connection stayed
 * open. An abort shows that this process stopped the request, and one more
 * attempt would only add one more timeout.
 *
 * @param {*} error the error that an attempt failed with
 * @returns {boolean} true if the peer closed the connection
 */
export function isConnectionReset(error) {
  if (!error) return false;
  if (typeof error === 'string') return CONNECTION_RESET_PATTERN.test(error);

  try {
    if (error.statusCode) return false;
    if (error.name === 'AbortError' || error.code === 'ABORT_ERR') return false;
    if (error.code === 'ECONNRESET') return true;
  }
  catch (readFailure) {
    return false;
  }

  return CONNECTION_RESET_PATTERN.test(errorText(error));
}

/**
 * Reads the shared deadline of an operation.
 *
 * @param {*} operation the Apollo operation
 * @returns {?number} the deadline, or null if the operation has none
 */
function deadlineOf(operation) {
  try {
    const deadline = operation.getContext()[SSR_DEADLINE_KEY];

    return isUsableTime(deadline) ? deadline : null;
  }
  catch (readFailure) {
    return null;
  }
}

/**
 * Reads the name of an operation.
 *
 * The name can be a getter, and a getter can fail. The name can also be a value
 * that is not a string. Therefore each test of the name must use this reader.
 * Code that only puts the name into a message or an address can read the
 * property, because a message accepts any value.
 *
 * @param {*} operation the Apollo operation
 * @returns {?string} the name, or null if the operation gives no usable name
 */
export function operationNameOf(operation) {
  try {
    const { operationName, } = operation;

    return typeof operationName === 'string' ? operationName : null;
  }
  catch (readFailure) {
    return null;
  }
}

/**
 * Reads the time that remains in the shared budget of an operation.
 *
 * @param {*} operation the Apollo operation
 * @param {number} [now] the current time. A test supplies this value.
 * @returns {?number} the time that remains, or null if there is no budget
 */
export function remainingBudgetMs(operation, now = Date.now()) {
  const deadline = deadlineOf(operation);

  if (deadline === null || !isUsableTime(now)) return null;

  return deadline - now;
}

/**
 * Reads the abort timeout for the next attempt.
 *
 * The result is the limit for one attempt. The result becomes smaller when less
 * of the shared budget remains, so that no attempt outlives the budget.
 *
 * @param {*} operation the Apollo operation
 * @param {number} [now] the current time. A test supplies this value.
 * @returns {number} the time that this attempt can use
 */
export function attemptTimeoutMs(operation, now = Date.now()) {
  const remaining = remainingBudgetMs(operation, now);

  if (remaining === null) return SSR_FETCH_TIMEOUT_MS;

  return Math.max(SSR_MIN_ATTEMPT_MS, Math.min(SSR_FETCH_TIMEOUT_MS, remaining));
}

/**
 * Puts the shared deadline on an operation, one time only.
 *
 * The retry rule and the abort signal both read this deadline. Therefore the
 * two always agree, and a later attempt cannot get a new budget.
 *
 * @param {*} operation the Apollo operation
 * @param {number} [now] the current time. A test supplies this value.
 * @returns {void}
 */
export function stampDeadline(operation, now = Date.now()) {
  if (deadlineOf(operation) !== null) return;

  const start = isUsableTime(now) ? now : Date.now();

  try {
    operation.setContext({ [SSR_DEADLINE_KEY]: start + SSR_RETRY_BUDGET_MS, });
  }
  catch (writeFailure) {
    // The operation refuses a new context. The retry then makes one attempt
    // only, and that result is safe.
  }
}

/**
 * Makes a test that accepts an operation if its name is in a given list.
 *
 * A Set holds the names, so the cost of one test does not grow with the length
 * of the list.
 *
 * @param {*} names names of the operations to accept
 * @returns {Function} a test that takes an operation and gives a boolean
 */
export function operationNameMatcher(names) {
  if (!names || typeof names[Symbol.iterator] !== 'function') return () => false;

  try {
    const accepted = new Set(names);

    return operation => {
      const name = operationNameOf(operation);

      return name !== null && accepted.has(name);
    };
  }
  catch (buildFailure) {
    return () => false;
  }
}

/**
 * Is this operation read-only?
 *
 * A second attempt sends the request again. A query only reads data, so a
 * second attempt is safe. A mutation changes data, and a second attempt can
 * apply that change two times. Therefore the policy retries a query only.
 *
 * If the operation gives no readable document, the result is false. The policy
 * then makes one attempt, and that result is safe.
 *
 * @param {*} operation the Apollo operation
 * @returns {boolean} true if each operation in the document is a query
 */
export function isQueryOperation(operation) {
  try {
    const { definitions, } = operation.query;

    if (!Array.isArray(definitions)) return false;

    const operations = definitions.filter(node => node.kind === 'OperationDefinition');

    return operations.length > 0 && operations.every(node => node.operation === 'query');
  }
  catch (readFailure) {
    return false;
  }
}

/**
 * Does this failed attempt accept one more attempt?
 *
 * The gate does not name operations. Each server-side GraphQL request travels
 * to the CDN, because `link` and `linkCluster` hold the same address. Therefore
 * each such request can get a closed connection. A gate that named operations
 * always missed some of them. Production showed this result for `TopNewsQuery`,
 * which uses the `/gql` path and not the `/gql/bs` path.
 *
 * @param {object} args the argument object
 * @param {*} args.error the error that the attempt failed with
 * @param {*} args.operation the Apollo operation
 * @param {boolean} args.isServerSide true during a server-side render
 * @param {number} [args.now] the current time. A test supplies this value.
 * @returns {boolean} true if one more attempt is of value
 */
export function shouldRetry({
  error,
  operation,
  isServerSide,
  now = Date.now(),
}) {
  if (isServerSide !== true) return false;
  if (!operation) return false;
  if (!isQueryOperation(operation)) return false;
  if (!isConnectionReset(error)) return false;

  const remaining = remainingBudgetMs(operation, now);

  return remaining !== null && remaining > SSR_RETRY_MIN_REMAINING_MS;
}

/**
 * Makes the link that puts the shared deadline on each operation.
 *
 * @param {object} args the argument object
 * @param {boolean} args.isServerSide true during a server-side render
 * @returns {object} an ApolloLink
 */
export function createSsrDeadlineLink({ isServerSide, }) {
  return new ApolloLink((operation, forward) => {
    if (isServerSide) stampDeadline(operation);

    return forward(operation);
  });
}

/**
 * Makes the link that retries a closed connection inside the shared deadline.
 *
 * The membership test is built one time here, and not for each attempt.
 *
 * @param {object} args the argument object
 * @param {boolean} args.isServerSide true during a server-side render
 * @returns {object} an ApolloLink
 */
export function createSsrRetryLink({ isServerSide, }) {
  return new RetryLink({
    delay: SSR_RETRY_DELAY,
    attempts: {
      max: SSR_RETRY_MAX_ATTEMPTS,
      // The link asks `shouldRetry`. Do not copy the rule to this place. A copy
      // makes the tests of `shouldRetry` give a result about unused code.
      retryIf: (error, operation) => shouldRetry({ error, operation, isServerSide, }),
    },
  });
}
